import React, { useEffect, useState } from 'react';
import {
  Box,
  Typography,
  Card,
  InputAdornment,
  TextField,
  InputBase,
  Paper,
  IconButton,
  Stack,
  CircularProgress,
  Alert,
} from '@mui/material';
import { useSelector } from 'react-redux';
import StudentsTable from './StudentsTable';
import SearchIcon from '@mui/icons-material/Search';
import { ThemeProvider, createTheme } from '@mui/material/styles';
import { useColorMode } from '@chakra-ui/react';
import axios from 'axios';

const StudentPage = () => {
  const { user } = useSelector((state) => state.Auth);
  const [students, setStudents] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [allStudents, setAllStudents] = useState([]);
  const { colorMode } = useColorMode();

  // Create MUI theme based on Chakra color mode
  const theme = createTheme({
    palette: {
      mode: colorMode,
      primary: {
        main: colorMode === 'light' ? '#3B82F6' : '#90CAF9',
      },
      background: {
        default: colorMode === 'light' ? '#ffffff' : '#121212',
        paper: colorMode === 'light' ? '#f9fafc' : '#1E1E1E',
      },
      text: {
        primary: colorMode === 'light' ? '#111827' : '#f3f4f6',
        secondary: colorMode === 'light' ? '#4B5563' : '#9CA3AF',
      },
    },
  });

  // Fetch student data from the agent students API
  const fetchStudentData = async () => {
    if (!user || !user._id) {
      console.log('Student Page - User not authenticated');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      console.log('Student Page - Fetching student data from agent API');

      const params = new URLSearchParams({
        page: '1',
        limit: '50', // Fetch up to 50 records
      });

      const response = await axios.get(
        `https://abro-care-backend.vercel.app/agent/students?${params}`,
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem('token_auth')}`,
          },
          withCredentials: true,
        },
      );

      console.log('Student API response:', response.data);

      const uniqueStudentsMap = new Map();

      if (
        response.data &&
        response.data.success &&
        Array.isArray(response.data.students)
      ) {
        // Process the students from the response
        response.data.students.forEach((student) => {
          if (student.name && !uniqueStudentsMap.has(student.name)) {
            uniqueStudentsMap.set(student.name, {
              _id: student._id || student.name,
              name: student.name,
              studentCode:
                student.studentCode ||
                'STU' + Math.floor(10000 + Math.random() * 90000),
              email: student.email || 'N/A',
              createdAt: student.createdAt,
              transactionCount: 1,
            });
          }
        });
      }

      // If no students found in direct API, fallback to the forex API
      if (uniqueStudentsMap.size === 0) {
        console.log(
          'Student Page - No students found in API, falling back to forex data',
        );

        // Fetch students from forex API
        const forexParams = new URLSearchParams({
          page: '1',
          limit: '50',
        });

        const forexResponse = await axios.get(
          `https://abro-care-backend.vercel.app/agent/forex?${forexParams}`,
          {
            headers: {
              Authorization: `Bearer ${localStorage.getItem('token_auth')}`,
            },
            withCredentials: true,
          },
        );

        if (
          forexResponse.data &&
          forexResponse.data.forexForms &&
          Array.isArray(forexResponse.data.forexForms)
        ) {
          // Process each forex form to extract student information
          forexResponse.data.forexForms.forEach((form) => {
            if (form.studentName && !uniqueStudentsMap.has(form.studentName)) {
              uniqueStudentsMap.set(form.studentName, {
                _id: form._id + '-student', // Create a unique ID
                name: form.studentName,
                studentCode: 'STU' + Math.floor(10000 + Math.random() * 90000), // Generate a student code if not available
                email: form.studentEmail || 'N/A',
                createdAt: form.date,
                country: form.country, // Additional info from forex
                currencyBooked: form.currencyBooked, // Additional info from forex
                amount: form.studentPaid, // Additional info from forex
                transactionCount: 1, // Track number of transactions
              });
            } else if (
              form.studentName &&
              uniqueStudentsMap.has(form.studentName)
            ) {
              // Update existing student with latest transaction if newer
              const existingStudent = uniqueStudentsMap.get(form.studentName);
              const existingDate = new Date(existingStudent.createdAt);
              const newDate = new Date(form.date);

              // Track transaction count
              existingStudent.transactionCount =
                (existingStudent.transactionCount || 0) + 1;

              // Update with newer transaction if applicable
              if (newDate > existingDate) {
                existingStudent.createdAt = form.date;
                existingStudent.country = form.country;
                existingStudent.currencyBooked = form.currencyBooked;
                existingStudent.amount = form.studentPaid;
              }

              uniqueStudentsMap.set(form.studentName, existingStudent);
            }
          });
        }
      }

      const uniqueStudents = Array.from(uniqueStudentsMap.values());
      console.log('Student Page - Extracted students:', uniqueStudents.length);

      // Sort students by date (most recent first)
      uniqueStudents.sort((a, b) => {
        const dateA = new Date(a.createdAt || 0);
        const dateB = new Date(b.createdAt || 0);
        return dateB - dateA;
      });

      setAllStudents(uniqueStudents);
      setStudents(uniqueStudents);
    } catch (error) {
      console.error('Error fetching student data:', error);
      setError('Failed to fetch student data. Please try again later.');
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = (e) => {
    const search = e.target.value.toLowerCase();
    if (search === '') {
      setStudents(allStudents || []);
    } else {
      const filteredStudents = allStudents?.filter((student) => {
        return student?.name?.toLowerCase().includes(search);
      });
      setStudents(filteredStudents || []);
    }
  };

  useEffect(() => {
    if (user && user._id) {
      // Fetch student data from the agent API
      fetchStudentData();

      // Add fallback to user students if available
      if (user?.students?.length > 0) {
        setAllStudents((prev) => {
          if (prev.length === 0) return user.students;
          return prev;
        });
        setStudents((prev) => {
          if (prev.length === 0) return user.students;
          return prev;
        });
      }
    }
  }, [user]);

  return (
    <ThemeProvider theme={theme}>
      <Box sx={{ p: 3 }}>
        <Stack
          direction={{ xs: 'column', sm: 'row' }}
          justifyContent="space-between"
          alignItems={{ xs: 'flex-start', sm: 'center' }}
          spacing={2}
          mb={3}
        >
          <Typography variant="h5" fontWeight="600" mb={{ xs: 2, sm: 0 }}>
            Manage Students
          </Typography>

          {!loading && (
            <Paper
              component="form"
              sx={{
                display: 'flex',
                alignItems: 'center',
                width: { xs: '100%', sm: 'auto' },
                p: '2px 8px',
                borderRadius: '30px',
                boxShadow: '0 2px 12px 0 rgba(0,0,0,0.05)',
              }}
            >
              <InputBase
                sx={{ ml: 1, flex: 1 }}
                placeholder="Search students..."
                onChange={handleSearch}
                inputProps={{ 'aria-label': 'search students' }}
              />
              <IconButton type="button" sx={{ p: '8px' }} aria-label="search">
                <SearchIcon />
              </IconButton>
            </Paper>
          )}
        </Stack>

        {error && (
          <Alert severity="error" sx={{ mb: 3 }}>
            {error}
          </Alert>
        )}

        <Box sx={{ mt: 3 }}>
          {loading ? (
            <Box
              display="flex"
              justifyContent="center"
              alignItems="center"
              height="200px"
            >
              <CircularProgress />
            </Box>
          ) : (
            <StudentsTable students={students} showForexDetails={true} />
          )}
        </Box>
      </Box>
    </ThemeProvider>
  );
};

export default StudentPage;
